import { DocumentNode, Kind, OperationDefinitionNode, parse, print } from 'graphql';

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** Collapses numeric and UUID segments so `/users/123` and `/users/456` group together. */
export const normalizeUrlPath = (url: string): string =>
    url
        .split('?')[0]
        .replace(UUID_PATTERN, ':id')
        .replace(/\/\d+(?=\/|$)/g, '/:id');

/** Replaces UUIDs and numbers so messages embedding IDs group into one issue. */
export const normalizeMessage = (message: string): string =>
    message.replace(UUID_PATTERN, '<id>').replace(/\d+/g, '<n>');

export interface OperationFirstDocument {
    document: string;
    operation?: string;
}

const MAX_CACHED_DOCUMENTS = 500;
const documentCache = new Map<string, OperationFirstDocument>();
const documentNodeCache = new WeakMap<DocumentNode, OperationFirstDocument>();

const isOperation = (definition: DocumentNode['definitions'][number]): definition is OperationDefinitionNode =>
    definition.kind === Kind.OPERATION_DEFINITION;

const reorder = (parsed: DocumentNode): OperationFirstDocument => {
    const operations = parsed.definitions.filter(isOperation);
    const others = parsed.definitions.filter((definition) => !isOperation(definition));
    const [operation] = operations;

    return {
        document: print({ ...parsed, definitions: [...operations, ...others] }),
        operation: operation && [operation.operation, operation.name?.value].filter(Boolean).join(' '),
    };
};

/** Prints a GraphQL document with the operation before its fragments, so size-truncation cuts fragments first. */
export const toOperationFirst = (document: DocumentNode | string): OperationFirstDocument => {
    if (typeof document !== 'string') {
        const cachedNode = documentNodeCache.get(document);
        if (cachedNode) return cachedNode;

        const result = reorder(document);
        documentNodeCache.set(document, result);
        return result;
    }

    const cached = documentCache.get(document);
    if (cached) return cached;

    let result: OperationFirstDocument;
    try {
        result = reorder(parse(document));
    } catch {
        result = { document };
    }

    if (documentCache.size >= MAX_CACHED_DOCUMENTS) documentCache.clear();
    documentCache.set(document, result);
    return result;
};
